import { useState, useEffect, useContext, createContext } from 'react';
import { onAuthStateChanged, User } from "firebase/auth";
import { FIREBASE_AUTH_INSTANCE } from '../Firebase';
import React from 'react';
import UserService from '../service/UserService';

interface AuthContextType {
    user: User | null;
    agreeToMarketing: boolean | null,
    managerStores: string[];
    roles: string[]; // Assuming roles is an array of strings
    loading: boolean; // Add loading state
    updateMarketingPreference: (value: boolean) => void;
}

// Create a context for the Auth state
const AuthContext = createContext<AuthContextType | null>(null);

// Custom hook to provide access to the user state
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

type Props = {
    children: React.ReactNode,
}

// AuthProvider component that wraps your app and provides user state
export const AuthProvider = (props: Props) => {
  const [user, setUser] = useState<User | null>(null);
  const [agreeToMarketing, setAgreeToMarketing] = useState<boolean | null>(null);
  const [managerStores, setManagerStores] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true); // Add loading state

  const updateMarketingPreference = (value: boolean) => {
    setAgreeToMarketing(value);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(FIREBASE_AUTH_INSTANCE(), async (user) => {
      setUser(user);

      if (user) {
        console.log("Auth hook: user is logged on.");
        
        // Log custom claims here
        try {
          const idTokenResult = await user.getIdTokenResult();
          console.log('All Custom Claims:', idTokenResult.claims);
        } catch (error) {
          console.error('Error getting custom claims:', error);
        }

        try {
          const roleRetrieval = await UserService.GetUserRoles(user);
          console.log("ROLE RETRIEVAL: ", roleRetrieval)
          setRoles(roleRetrieval.permissions || []);
          setManagerStores(roleRetrieval.orderManagement || [])
          setAgreeToMarketing(roleRetrieval.agreeToMarketing || false)
        } catch (error) {
          console.error("Error fetching roles:", error);
        }
      } else {
        console.log("Auth hook: User is not logged in");
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, roles, managerStores, agreeToMarketing, loading, updateMarketingPreference }}>
      {props.children}
    </AuthContext.Provider>
  );
};